// src/pages/admin/AdminNoivos.js

import React, { useEffect, useState } from 'react';
import {
  getTurmasNoivos, createTurmaNoivos, updateTurmaNoivos, deleteTurmaNoivos,
  getInscricoesNoivos, updateStatusNoivos,
} from '../../services/api';
import { formatDate } from '../../utils/helpers';
import Toast from '../../components/common/Toast';
import PrintFichasNoivos from '../../components/admin/PrintFichasNoivos';
import GerarCertificadosNoivos from '../../components/admin/GerarCertificadosNoivos';

const STATUS_OPTS = ['Pendente', 'Aprovado', 'Reprovado', 'Concluído'];
const initialTurma = { nome: '', dataInicio: '', dataFinal: '' };

const AdminNoivos = () => {
  const [turmas, setTurmas] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);
  const [form, setForm] = useState(initialTurma);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState('turmas');
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showCert, setShowCert] = useState(false);

  const loadTurmas = () => getTurmasNoivos().then(r => setTurmas(r?.data || [])).catch(() => {});
  const loadInscricoes = () => getInscricoesNoivos().then(r => setInscricoes(r?.data || [])).catch(() => {});

  useEffect(() => { loadTurmas(); loadInscricoes(); }, []);

  const pendentesCount = inscricoes.filter(i => i.status === 'Pendente').length;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await updateTurmaNoivos({ ...form, id: editId });
        setToast({ msg: 'Turma atualizada!', type: 'success' });
      } else {
        await createTurmaNoivos(form);
        setToast({ msg: 'Turma criada!', type: 'success' });
      }
      setForm(initialTurma);
      setEditId(null);
      setShowForm(false);
      loadTurmas();
    } catch {
      setToast({ msg: 'Erro ao salvar turma.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta turma?')) return;
    try {
      await deleteTurmaNoivos(id);
      setToast({ msg: 'Turma excluída.', type: 'success' });
      loadTurmas();
    } catch {
      setToast({ msg: 'Erro ao excluir.', type: 'error' });
    }
  };

  const handleEdit = (t) => {
    setForm({ nome: t.nome || '', dataInicio: t.dataInicio || '', dataFinal: t.dataFinal || '' });
    setEditId(t.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatus = async (id, status) => {
    try {
      await updateStatusNoivos(id, status);
      setToast({ msg: 'Status atualizado!', type: 'success' });
      loadInscricoes();
    } catch {
      setToast({ msg: 'Erro ao atualizar status.', type: 'error' });
    }
  };

  const cancelar = () => {
    setShowForm(false);
    setEditId(null);
    setForm(initialTurma);
  };

  const tabStyle = (active) => ({
    padding: '0.75rem 1.5rem', border: 'none', cursor: 'pointer',
    borderBottom: active ? '3px solid var(--gold)' : '3px solid transparent',
    background: 'transparent', fontWeight: active ? 700 : 400,
    color: active ? 'var(--navy)' : 'var(--gray-400)',
    fontFamily: 'var(--font-body)', fontSize: '0.9rem', transition: 'all 0.15s',
  });

  return (
    <div style={{ padding: 'var(--space-2xl)' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Modais */}
      {showPrint && <PrintFichasNoivos inscricoes={inscricoes} onClose={() => setShowPrint(false)} />}
      {showCert  && <GerarCertificadosNoivos inscricoes={inscricoes} turmas={turmas} onClose={() => setShowCert(false)} />}

      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Curso de Noivos</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>Gerencie turmas e inscrições</p>
      </div>

      {/* Abas */}
      <div style={{ borderBottom: '1px solid var(--gray-200)', marginBottom: 'var(--space-xl)', display: 'flex' }}>
        <button style={tabStyle(tab === 'turmas')} onClick={() => setTab('turmas')}>
          Turmas ({turmas.length})
        </button>
        <button style={tabStyle(tab === 'inscricoes')} onClick={() => setTab('inscricoes')}>
          Inscrições ({inscricoes.length})
        </button>
      </div>

      {/* ── Turmas ── */}
      {tab === 'turmas' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-lg)' }}>
            <button className="btn btn-primary btn-sm" onClick={() => { cancelar(); setShowForm(true); }}>
              + Nova Turma
            </button>
          </div>

          {showForm && (
            <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-md)', marginBottom: 'var(--space-xl)', border: '2px solid var(--gold-pale)' }}>
              <h3 style={{ marginBottom: 'var(--space-lg)', fontSize: '1rem' }}>
                {editId ? '✏️ Editar Turma' : '+ Nova Turma'}
              </h3>
              <form onSubmit={handleSave} noValidate>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Nome <span className="required">*</span></label>
                    <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                      className="form-input" placeholder="Ex: Setembro 2026" required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Data de Início</label>
                    <input type="date" value={form.dataInicio} onChange={e => setForm(f => ({ ...f, dataInicio: e.target.value }))}
                      className="form-input" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Data de Encerramento</label>
                    <input type="date" value={form.dataFinal} onChange={e => setForm(f => ({ ...f, dataFinal: e.target.value }))}
                      className="form-input" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-sm"
                    style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}
                    onClick={cancelar}>Cancelar</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Salvando...' : editId ? 'Salvar' : 'Criar Turma'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {turmas.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', textAlign: 'center', padding: '3rem' }}>Nenhuma turma cadastrada.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--space-lg)' }}>
              {turmas.map((t, i) => (
                <div key={i} style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)', boxShadow: 'var(--shadow-sm)', borderTop: '4px solid var(--gold)' }}>
                  <h4 style={{ marginBottom: '0.75rem' }}>{t.nome}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1rem' }}>
                    {t.dataInicio && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                        <span style={{ color: 'var(--gray-400)' }}>Início</span>
                        <strong>{formatDate(t.dataInicio)}</strong>
                      </div>
                    )}
                    {t.dataFinal && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                        <span style={{ color: 'var(--gray-400)' }}>Encerramento</span>
                        <strong>{formatDate(t.dataFinal)}</strong>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-sm btn-navy" style={{ flex: 1 }} onClick={() => handleEdit(t)}>Editar</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t.id)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Inscrições ── */}
      {tab === 'inscricoes' && (
        <>
          {/* Botões de ação */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginBottom: 'var(--space-lg)', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowPrint(true)}
              disabled={pendentesCount === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: pendentesCount > 0 ? 'var(--navy)' : 'var(--gray-200)',
                color: pendentesCount > 0 ? 'white' : 'var(--gray-400)',
                border: 'none', borderRadius: 'var(--radius-md)',
                padding: '0.6rem 1.25rem', cursor: pendentesCount > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem', fontWeight: 600, fontFamily: 'var(--font-body)',
              }}>
              🖨️ Imprimir Fichas
              <span style={{ background: pendentesCount > 0 ? 'var(--gold)' : 'var(--gray-300)', color: 'var(--navy)', borderRadius: '99px', padding: '0.1rem 0.5rem', fontSize: '0.8rem', fontWeight: 700 }}>
                {pendentesCount}
              </span>
            </button>
            <button
              onClick={() => setShowCert(true)}
              disabled={pendentesCount === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: pendentesCount > 0 ? '#1e7d4f' : 'var(--gray-200)',
                color: pendentesCount > 0 ? 'white' : 'var(--gray-400)',
                border: 'none', borderRadius: 'var(--radius-md)',
                padding: '0.6rem 1.25rem', cursor: pendentesCount > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem', fontWeight: 600, fontFamily: 'var(--font-body)',
              }}>
              🎓 Gerar Certificados
              <span style={{ background: pendentesCount > 0 ? 'rgba(255,255,255,0.25)' : 'var(--gray-300)', color: pendentesCount > 0 ? 'white' : 'var(--gray-400)', borderRadius: '99px', padding: '0.1rem 0.5rem', fontSize: '0.8rem', fontWeight: 700 }}>
                {pendentesCount}
              </span>
            </button>
          </div>

          {inscricoes.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', textAlign: 'center', padding: '3rem' }}>Nenhuma inscrição registrada.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Turma</th>
                    <th>Noivo</th>
                    <th>Noiva</th>
                    <th>Telefones</th>
                    <th>Casamento</th>
                    <th>Documentos</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {inscricoes.map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: '0.82rem' }}>{s.turmaNome || '—'}</td>
                      <td>
                        <strong style={{ fontSize: '0.875rem', display: 'block' }}>{s.nomeNoivo || '—'}</strong>
                        {s.comunhaoNoivo && <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>Comunhão: {s.comunhaoNoivo}</span>}
                      </td>
                      <td>
                        <strong style={{ fontSize: '0.875rem', display: 'block' }}>{s.nomeNoiva || '—'}</strong>
                        {s.comunhaoNoiva && <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>Comunhão: {s.comunhaoNoiva}</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {s.telefoneNoivo && <div>📱 {s.telefoneNoivo}</div>}
                        {s.telefoneNoiva && <div>📱 {s.telefoneNoiva}</div>}
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {s.dataCasamento ? formatDate(s.dataCasamento) : '—'}
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {s.urlDocNoivo    && <a href={s.urlDocNoivo}    target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--navy)' }}>📄 Noivo</a>}
                        {s.urlDocNoiva   && <a href={s.urlDocNoiva}   target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--navy)' }}>📄 Noiva</a>}
                        {s.urlComprovante && <a href={s.urlComprovante} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--navy)' }}>📄 Comprovante</a>}
                      </td>
                      <td>
                        <select value={s.status || 'Pendente'}
                          onChange={e => handleStatus(s.id, e.target.value)}
                          className="form-select" style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem', minWidth: 110 }}>
                          {STATUS_OPTS.map(st => <option key={st} value={st}>{st}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminNoivos;
